import { PrimaryBigButton } from 'components/ReusableComponents/Button/BigButtons'
import { SecondarySmallButton } from 'components/ReusableComponents/Button/SmallButtons'
import AddIcon from 'components/ReusableComponents/Icons/Add'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import HotelSetupFormGroup from 'features/HotelSetup/FormGroup'
import ManageUserDialog from 'features/ManageUserDialog'
import { useCallback, useEffect, useState } from 'react'
import { useToasts } from 'react-toast-notifications'
import contractApi from 'services/api/contracts'
import { formatPhoneNumber } from 'utils/helpers'

import styles from './HotelStaff.module.scss'

/**
 *
 * @typedef {Object} User
 * @property {string} userId
 * @property {string} hotelName
 * @property {string} merchantName
 * @property {string} name
 * @property {string} phone
 * @property {string} email
 * @property {number} utcOffSet
 * @property {string} tzTimeZoneName
 * @property {boolean} isInvoiceRecipient
 * @property {boolean} isPmsIntegrationContact
 * @property {string} lastActivityOn
 * @property {null} lastClickedOn
 * @property {string} position
 */

const HotelSetupHotelStaff = ({ onStepComplete }) => {
  const { addToast } = useToasts()
  const [isLoading, setIsLoading] = useState(true)
  const [isManageUserDialogState, setIsManageUserDialogState] = useState({
    isOpen: false,
    type: null
  })

  const [gmUserId, setGmUserId] = useState()
  const [pmsIntegrationContactUserId, setPmsIntegrationContactUserId] = useState()
  const [accountPayableUserId, setAccountPayableUserId] = useState()

  const [hotelStaffData, setHotelStaffData] = useState({
    pmsIntegrationContactUserId: null,
    accountPayableUserId: null,
    users: []
  })

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true)

      const result = await contractApi.hotelStaff.get()
      setHotelStaffData(result)
      setGmUserId(result.gmUserId || undefined)
      setPmsIntegrationContactUserId(result.pmsIntegrationContactUserId || undefined)
      setAccountPayableUserId(result.accountPayableUserId || undefined)
      setPmsIntegrationContactUserId(result.pmsIntegrationContactUserId || undefined)
      setAccountPayableUserId(result.accountPayableUserId || undefined)
    } catch {
      addToast('Something went wrong', {
        appearance: 'error',
        autoDismiss: true
      })
    } finally {
      setIsLoading(false)
    }
  }, [addToast])

  const handleAddedUser = user => {
    const type = isManageUserDialogState.type
    setIsManageUserDialogState({ isOpen: false })

    setHotelStaffData(prevState => ({
      ...prevState,
      users: [
        ...prevState.users,
        {
          userId: user.id,
          name: user.firstName + ' ' + user.lastName,
          email: user.email,
          phone: user.phone
        }
      ]
    }))

    switch (type) {
      case 'gm':
        setGmUserId(user.id)
        break
      case 'pms':
        setPmsIntegrationContactUserId(user.id)
        break
      case 'invoice':
        setAccountPayableUserId(user.id)
        break
      default:
        break
    }
  }

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const generalManager = hotelStaffData.users.find(user => user.userId === gmUserId)

  const pmsIntegrationContact = hotelStaffData.users.find(
    user => user.userId === pmsIntegrationContactUserId
  )

  // const invoiceContact = hotelStaffData.users.find(
  //   user => user.userId === hotelStaffData.accountPayableUserId
  // )

  const invoiceContact = hotelStaffData.users.find(
    user => user.userId === accountPayableUserId
  )

  const isAllStaffFilled = gmUserId && pmsIntegrationContactUserId && accountPayableUserId

  const onSubmit = () => {
    if (!isAllStaffFilled) {
      addToast('Please select users for all positions', {
        appearance: 'error',
        autoDismiss: true
      })
      return
    }

    setIsLoading(true)
    contractApi.hotelStaff
      .update({
        pmsIntegrationContactUserId,
        accountPayableUserId,
        gmUserId
      })
      .then(() => {
        addToast('Hotel staff updated successfully', {
          appearance: 'success',
          autoDismiss: true
        })

        onStepComplete()
      })
      .catch(() => {
        addToast('Something went wrong', {
          appearance: 'error',
          autoDismiss: true
        })
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  return (
    <>
      {isManageUserDialogState.isOpen && (
        <ManageUserDialog
          handleClose={() => setIsManageUserDialogState({ isOpen: false })}
          handleAddedUser={handleAddedUser}
          isAddingDuringSetup
        />
      )}

      <div className={styles.container}>
        <div className={styles.sections}>
          <HotelSetupFormGroup
            contentPadding="30px"
            title="General manager"
            description="Primary user of the dashboard"
          >
            {generalManager ? (
              <FilledStaffInfo
                name={generalManager.name}
                email={generalManager.email}
                phone={generalManager.phone}
                onClickRemove={() => setGmUserId(null)}
              />
            ) : (
              <AddNewUser
                onAddNewUser={() =>
                  setIsManageUserDialogState({ isOpen: true, type: 'gm' })
                }
                selectedUserId={gmUserId}
                onSelectUser={id => setGmUserId(id)}
                users={hotelStaffData.users}
              />
            )}
          </HotelSetupFormGroup>
          <HotelSetupFormGroup
            contentPadding="30px"
            title="PMS integration contact"
            description="We'll contact this person for integration"
          >
            {pmsIntegrationContact ? (
              <FilledStaffInfo
                name={pmsIntegrationContact.name}
                email={pmsIntegrationContact.email}
                phone={pmsIntegrationContact.phone}
                onClickRemove={() => setPmsIntegrationContactUserId(null)}
              />
            ) : (
              <AddNewUser
                onAddNewUser={() =>
                  setIsManageUserDialogState({ isOpen: true, type: 'pms' })
                }
                selectedUserId={pmsIntegrationContactUserId}
                onSelectUser={id => setPmsIntegrationContactUserId(id)}
                users={hotelStaffData.users}
              />
            )}
          </HotelSetupFormGroup>
          <HotelSetupFormGroup
            contentPadding="30px"
            title="Invoice contact"
            description="Invoices will be issued to this person"
          >
            {invoiceContact ? (
              <FilledStaffInfo
                name={invoiceContact.name}
                email={invoiceContact.email}
                phone={invoiceContact.phone}
                onClickRemove={() => setAccountPayableUserId(null)}
              />
            ) : (
              <AddNewUser
                onAddNewUser={() =>
                  setIsManageUserDialogState({ isOpen: true, type: 'invoice' })
                }
                selectedUserId={accountPayableUserId}
                onSelectUser={id => setAccountPayableUserId(id)}
                users={hotelStaffData.users}
              />
            )}
          </HotelSetupFormGroup>
        </div>
        <div className={styles.bottomBar}>
          <div className={styles.bottomBarContainer}>
            <div className={styles.bottomBarButtonsHolder}>
              <PrimaryBigButton disabled={isLoading} onClick={onSubmit}>
                {isLoading ? 'Saving...' : 'Next'}
              </PrimaryBigButton>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

/**
 *
 * @typedef {Object} AddNewUserProps
 * @property {User[]} users
 * @property {string} selectedUserId
 * @property {() => void} onAddNewUser
 * @property {(id: string) => void} onSelectUser
 *
 * @param {AddNewUserProps} props
 * @returns
 */
const AddNewUser = props => {
  const { users, selectedUserId, onAddNewUser, onSelectUser } = props

  return (
    <div className={styles.addNewUser}>
      <PrimaryBigButton className={styles.button} onClick={onAddNewUser}>
        <AddIcon className={styles.addIcon} />
        Add a new user
      </PrimaryBigButton>

      <div className={styles.dividerWrapper}>
        <div className={styles.line} />
        <p className={styles.text}>or</p>
        <div className={styles.line} />
      </div>
      <Dropdown
        className={styles.dropdown}
        value={selectedUserId}
        onChange={e => onSelectUser(e.target.value)}
        defaultVal="Select existing user"
        options={users.reduce((acc, user) => {
          acc[user.userId] = user.name
          return acc
        }, {})}
      />
    </div>
  )
}

/**
 *
 * @typedef {Object} FilledStaffInfoProps
 * @property {string} name
 * @property {string} email
 * @property {string} phone
 * @property {() => void} onClickRemove
 *
 * @param {FilledStaffInfoProps} props
 * @returns
 */
const FilledStaffInfo = ({ name, email, phone, onClickRemove }) => {
  return (
    <div className={styles.filledStaffInfoWrapper}>
      <div>
        <p className={styles.name}>{name}</p>
        <div className={styles.emailPhoneRow}>
          <div className={styles.email}>
            <p className={styles.value}>{email}</p>
          </div>
          {phone && (
            <div className={styles.phone}>
              <p className={styles.value}>{formatPhoneNumber(phone)}</p>
            </div>
          )}
        </div>
      </div>
      <SecondarySmallButton className={styles.removeButton} onClick={onClickRemove}>
        Remove user
      </SecondarySmallButton>
    </div>
  )
}

export default HotelSetupHotelStaff
